import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserAvatar } from "./UserAvatar";
import { checkPhotoFile, loadPhotoDownscaled } from "./PhotoCropper";

const VIEW = 280;   // crop circle size on screen (px)
const OUT = 512;    // exported square (px)

const loadImage = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

// Pan (drag) + zoom inside a circular mask; exports the visible square as a 512 px JPEG data URL.
function CropDialog({ src, onClose, onSave, saving }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => { loadImage(src).then(setImg).catch(() => { toast.error("Foto tidak bisa dibuka"); onClose(); }); }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = img ? VIEW / Math.min(img.width, img.height) : 1;  // "cover" scale at zoom 1
  const w = img ? img.width * base * zoom : 0, h = img ? img.height * base * zoom : 0;
  const clamp = (p) => ({ x: Math.min(0, Math.max(VIEW - w, p.x)), y: Math.min(0, Math.max(VIEW - h, p.y)) });
  useEffect(() => { if (img) setPos({ x: (VIEW - img.width * base) / 2, y: (VIEW - img.height * base) / 2 }); }, [img]); // eslint-disable-line react-hooks/exhaustive-deps
  const zoomTo = (z) => {  // keep the circle's centre fixed while zooming
    const k = z / zoom, nw = img.width * base * z, nh = img.height * base * z;
    const nx = VIEW / 2 - (VIEW / 2 - pos.x) * k, ny = VIEW / 2 - (VIEW / 2 - pos.y) * k;
    setZoom(z); setPos({ x: Math.min(0, Math.max(VIEW - nw, nx)), y: Math.min(0, Math.max(VIEW - nh, ny)) });
  };

  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, ...pos }; };
  const move = (e) => { if (drag.current) setPos(clamp({ x: drag.current.x + e.clientX - drag.current.sx, y: drag.current.y + e.clientY - drag.current.sy })); };
  const up = () => { drag.current = null; };

  const save = () => {
    const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    const s = base * zoom;
    c.getContext("2d").drawImage(img, -pos.x / s, -pos.y / s, VIEW / s, VIEW / s, 0, 0, OUT, OUT);
    onSave(c.toDataURL("image/jpeg", 0.9));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-sm" data-testid="avatar-crop-dialog">
        <DialogHeader><DialogTitle>Atur foto profil</DialogTitle><DialogDescription>Geser untuk memposisikan, gunakan slider untuk memperbesar.</DialogDescription></DialogHeader>
        <div className="mx-auto overflow-hidden rounded-full bg-slate-100 ring-4 ring-[#2F6BFF]/20" style={{ width: VIEW, height: VIEW, touchAction: "none", cursor: "grab" }}
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="avatar-crop-area">
          {img ? <img src={src} alt="" draggable={false} className="pointer-events-none max-w-none select-none" style={{ width: w, height: h, transform: `translate(${pos.x}px, ${pos.y}px)` }} />
            : <div className="flex h-full items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>}
        </div>
        <div className="flex items-center gap-3 px-2"><ZoomIn size={16} className="text-slate-400" />
          <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={([z]) => img && zoomTo(z)} data-testid="avatar-crop-zoom" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50" data-testid="avatar-crop-cancel">Batal</button>
          <button onClick={save} disabled={!img || saving} className="flex items-center gap-1.5 rounded-xl bg-[#2F6BFF] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#2558d6] disabled:opacity-60" data-testid="avatar-crop-save">
            {saving && <Loader2 size={14} className="animate-spin" />} Simpan foto
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AvatarUploader() {
  const { user, refreshUser } = useAuth();
  const input = useRef(null);
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const err = checkPhotoFile(f);
    if (err) return toast.error(err);
    loadPhotoDownscaled(f, 1600).then(setSrc).catch(() => toast.error("Foto tidak bisa dibaca. Coba JPG atau PNG."));  // downscaled before pan/zoom
  };
  const upload = async (data) => {
    setBusy(true);
    try { await api.post("/me/avatar", { data }); await refreshUser(); setSrc(null); toast.success("Foto profil diperbarui"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan foto"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/me/avatar"); await refreshUser(); toast.success("Foto profil dihapus"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus foto"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col items-center gap-1.5" data-testid="avatar-uploader">
      <button type="button" onClick={() => input.current?.click()} disabled={busy} className="group relative rounded-full" title="Ganti foto profil" data-testid="avatar-change-btn">
        <UserAvatar name={user?.name} avatar={user?.avatar} size={72} bg="linear-gradient(135deg,#00D1FF,#7C3AED)" testid="profile-avatar" />
        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100">{busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}</span>
        <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2F6BFF] text-white ring-2 ring-white"><Camera size={12} /></span>
      </button>
      {user?.avatar_custom && <button type="button" onClick={remove} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 transition-colors hover:text-[#EF4444]" data-testid="avatar-remove-btn"><Trash2 size={11} /> Hapus foto</button>}
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="avatar-file-input" />
      {src && <CropDialog src={src} saving={busy} onClose={() => setSrc(null)} onSave={upload} />}
    </div>
  );
}
